import { Controller } from "@hotwired/stimulus";
import { Chart, registerables } from "chart.js";
import "chartjs-adapter-date-fns";
import { es } from "date-fns/locale";
import annotationPlugin from "chartjs-plugin-annotation";

Chart.register(...registerables, annotationPlugin);

export default class extends Controller {
  static targets = ["canvas"];

  static values = {
    currency: { type: String, default: "EUR" },
    pricedata: Array,
    notify: Number,
    minimumLabel: String,
    notifyLabel: String,
    title: String,
  };

  connect() {
    if (!this.hasCanvasTarget || this.pricedataValue.length === 0) return;

    const chartAreaBorder = {
      id: "chartAreaBorder",
      beforeDraw(chart, _args, options) {
        const {
          ctx,
          chartArea: { left, top, width, height },
        } = chart;
        ctx.save();
        ctx.strokeStyle = options.borderColor;
        ctx.lineWidth = options.borderWidth;
        ctx.setLineDash(options.borderDash || []);
        ctx.lineDashOffset = options.borderDashOffset;
        ctx.strokeRect(left, top, width, height);
        ctx.restore();
      },
    };
    const values = this.pricedataValue
      .map((point) => Number(point.y))
      .filter(Number.isFinite);
    if (values.length === 0) return;

    const observedMin = Math.min(...values);
    const observedMax = Math.max(...values);
    const range = observedMax - observedMin;
    const padding =
      range > 0 ? range * 0.1 : Math.max(Math.abs(observedMin) * 0.1, 1);
    const min = Math.max(0, observedMin - padding);
    const max = observedMax + padding;
    const priceFormatter = new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: this.currencyValue,
      minimumFractionDigits: 2,
    });

    const alertValue = this.notifyValue;
    const data = {
      datasets: [
        {
          label: this.minimumLabelValue,
          data: this.pricedataValue,
          borderColor: "rgb(75, 192, 192)",
          backgroundColor: "hsl(252, 82.9%, 67.8%)",
          fill: false,
          pointStyle: false,
          stepped: "after",
        },
      ],
    };

    const annotation = {
      type: "line",
      label: {
        display: true,
        backgroundColor: "hsl(252, 82.9%, 77.8%)",
        borderColor: "hsl(252, 82.9%, 67.8%)",
        borderRadius: 5,
        borderWidth: 2,
        content: (_ctx) => this.notifyLabelValue.replace("%{price}", priceFormatter.format(alertValue)),
        rotation: "auto",
      },
      scaleID: "y",
      adjustScaleRange: true,
      value: alertValue,
    };

    var configLineChart = {
      type: "line",
      data,
      options: {
        locale: "es-ES",
        scales: {
          x: {
            adapters: {
              date: {
                locale: es,
              },
            },
            type: "time",
            time: {
              unit: "month",
            },
          },
          y: {
            type: "linear",
            min,
            max,
            ticks: {
              callback: (value) => priceFormatter.format(value),
            },
          },
        },
        maintainAspectRatio: true,
        aspectRatio: 1.75,
        interaction: {
          intersect: false,
          axis: "x",
        },
        plugins: {
          tooltip: {
            callbacks: {
              label: (context) => priceFormatter.format(context.parsed.y),
            },
          },
          title: {
            display: true,
            text: this.titleValue,
            position: "bottom",
          },
          legend: {
            display: false,
          },
          chartAreaBorder: {
            borderColor: "#e5e5e5",
            borderWidth: 2,
          },
        },
      },
      plugins: [chartAreaBorder],
    };

    if (alertValue > 0) {
      configLineChart.options.plugins["annotation"] = {
        annotations: {
          annotation,
        },
      };
    }
    this.chart = new Chart(this.canvasTarget, configLineChart);
  }

  disconnect() {
    this.chart?.destroy();
  }
}
